import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from 'styled-components'

import { useBowtieLanguage, useBowtieStateds } from '../../../../../../../hooks'
import { IReduxReducers } from '../../../../../../../store'
import { barrierHeight, barrierWidth, bowtieTheme, unfocusOpacity } from '../../../../../../../utils/constants'
import { BowtieStatusContext } from '../../../../../../BowtieStatusContext'
import { ConsequenceView, ThreatView } from '../../../../../../BowtieViews'
import { TextContainer } from './styles'

interface IProps {
    x: number
    y: number
    barriersLength: number
    type: 'threat' | 'consequence'
    view: ThreatView | ConsequenceView
    onClick: () => void
}

interface IResumeBarrierGetter {
    name: string
    customName: string
    color: string
    total: number
}

export const ResumeBarrier: React.FC<IProps> = ({ x, y, barriersLength, type, onClick, view }) => {
    const [currentStatus, setCurrentStatus] = useState<IResumeBarrierGetter>({
        name: 'Unknown',
        customName: null,
        color: bowtieTheme.barrierColors.unknown,
        total: 0
    })

    const barrierEvents = useBowtieStateds(view.barriersView)
    const i18n = useBowtieLanguage()

    const { highlightDegradedBarriers } = useSelector((state: IReduxReducers) => state.highlightDegradedBarriers)

    const theme = useTheme()

    const { getResumeBarriersLengthAndTypeStatusEventId } = useContext(BowtieStatusContext)

    const getStatusId = (): void => {
        const ids = barrierEvents
            ?.filter(el => el)
            .filter(el => el.status !== 'UNMONITORED')
            .map(event => event?.status_id)
            .filter(e => e)
        if (!ids?.length) {
            setCurrentStatus({
                name: i18n('Unknown'),
                customName: null,
                color: bowtieTheme.barrierColors.unknown,
                total: view.barriersView.filter(el => el.barrier.barrierType.barrierTypeSpecial !== 'UNMONITORED')
                    .length
            })

            return
        }

        const statusList = getResumeBarriersLengthAndTypeStatusEventId(ids)

        for (const [idx, status] of statusList.entries()) {
            if (idx === statusList.length - 1) {
                const inactiveStatus = statusList.find(el => el.name === 'Inactive')
                
                setCurrentStatus({ ...inactiveStatus, color: status.color })
                break
            }

            if (status.total > 0) {
                setCurrentStatus(status)
                break
            }
        }
    }

    useEffect(() => {
        getStatusId()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [barrierEvents])

    return (
        <g
            transform={`translate(${type === 'consequence' ? x - barrierWidth : x}, ${y})`}
            onClick={onClick}
            cursor="pointer"
            fill={theme['gray-0']}
            opacity={highlightDegradedBarriers ? (currentStatus.total ? 1 : unfocusOpacity) : 1}
        >
            {/* TODO: this is not Figma's standard already spoke with Debora and ask to redesign figma resumeBarrier because it is with wrong dimensions. When figma is ready change it */}

            <rect
                width={barrierWidth}
                height={barrierHeight}
                stroke={currentStatus.color ?? 'gray'}
                strokeWidth={1}
                rx={3}
            />

            <path d="M 100 5 V 3 q 0 -3 -3 -3 h -94 q -3 0 -3 3 V 5 z" fill={currentStatus.color ?? 'gray'} />
            <foreignObject
                x={0}
                y={4}
                width={barrierWidth}
                height={barrierHeight - 4}
                style={{
                    overflow: 'hidden'
                }}
            >
                <TextContainer
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: barrierWidth,
                        height: barrierHeight - 4
                    }}
                >
                    <b style={{ fontSize: 12, fontFamily: theme['font-bold'] }}>
                        {currentStatus.total}/{barriersLength}
                    </b>
                    {i18n(currentStatus.customName ? currentStatus.customName : currentStatus.name)}
                </TextContainer>
            </foreignObject>
        </g>
    )
}
